import React from 'react'

import { Stack } from '@mui/material'

import { PlanType } from '@common-sense-privacy/common/src/api/endpoints/stripe/plans/types'

import {
  transformPlans,
  getPlanType,
  isSealPlan as checkIsSealPlan,
} from '@/utils/stripe'

import PlanCard from './Card'

import { Props } from './types'

const SEAL_PLAN_FEATURES = [
  'It follows the best practices when it comes to their most important privacy concerns',
  'It provides full transparency, allowing them to make informed decisions',
  "It's backed by Common Sense Media, and the world's largest database of best practices regarding privacy",
]

function PlanCards({
  plans,
  subscription,
  requiresPaidPlan,
  onContinue,
  onSelect,
  hasMultipleProducts,
}: Props): React.ReactElement {
  const defaultPlans = plans.filter(plan => plan.isDefault)
  const transformedPlans = transformPlans({
    plans: defaultPlans,
    subscription,
  })

  const userPlanType = subscription ? getPlanType(subscription) : undefined
  const isUserOnSealPlan = userPlanType ? checkIsSealPlan(userPlanType) : false

  const getDisplayPrice = (plan: typeof transformedPlans[number]) => {
    if (plan.type === PlanType.Starter) {
      return 'Get Started'
    }

    return `Starting at ${plan.price}`
  }

  return (
    <Stack
      direction={{
        xs: 'column',
        md: 'row',
      }}
      spacing={2}
    >
      {transformedPlans.map(plan => {
        const shouldShowSelectButton = !hasMultipleProducts && !plan.isActive && !plan.scheduledForDowngrade && !!onSelect && (!plan.isFree || !requiresPaidPlan)
        const isSealPlan = checkIsSealPlan(plan.type)
        const shouldShowDisabledContactSales = isUserOnSealPlan && !isSealPlan && !plan.isActive

        return (
          <PlanCard
            key={plan.id}
            planTypeTitle={plan.name}
            price={getDisplayPrice(plan)}
            interval={plan.billingInterval}
            listItems={plan.features.map(feature => feature.name ?? '')}
            isActive={plan.isActive}
            showContinueButton={plan.isActive && (plan.isFree ? !requiresPaidPlan : false) && !!onContinue}
            showSelectButton={shouldShowSelectButton && !shouldShowDisabledContactSales}
            onContinue={onContinue}
            onSelect={() => onSelect && onSelect(plan.id, plan.priceId)}
            scheduledForDowngrade={plan.scheduledForDowngrade}
            downgradeHelperText={plan.downgradeHelperText}
            showDisabledContactSales={shouldShowDisabledContactSales}
          />
        )
      })}
      <PlanCard
        planCopy="Our Privacy Seal helps privacy-conscious businesses earn customer trust and stand out from the crowd. When customers see the Privacy Seal on your product, they'll know that:"
        planTypeTitle='Common Sense Privacy Seal'
        price='Contact Sales'
        interval=''
        listItems={SEAL_PLAN_FEATURES}
        isActive={isUserOnSealPlan}
        showContinueButton={false}
        showSelectButton={!hasMultipleProducts && !isUserOnSealPlan}
        selectButtonHref='/contact-support?topic=Common%20Sense%20Privacy%20Seal&inquiry=I%27d%20like%20to%20sign%20up%20for%20the%20Seal%20plan.'
      />
    </Stack>
  )
}

export default React.memo(PlanCards)
